import { LanguageMaps } from '../../../../api/language'
import { ContentLanguageTranslation } from '../../../../types/InternalAnalystComment'
import { InternalCategoryFeature, InternalCategoryFeatureChoice } from '../../../../types/InternalCategory'
import { categoryLanguages } from '../../helpers/categoryLanguageHelpers'
import {
  AssignedValues,
  FeatureExample,
  FeatureExampleItem,
  FeatureExampleItemType,
  FeatureSaveBody,
  FeatureSaveChoice,
  FeatureSaveResult,
  LanguageValues,
} from '../../types/Categories'
import { assignTranslation, byLanguage } from './categoryHelpers'

type Named = { id: string; name: string }
type MarketOption = Named & { iso: string }
type Translation = Partial<Record<string, string | null | undefined>> | null | undefined

let keyCounter = 0
const newKey = (prefix: string) => `${prefix}-new-${++keyCounter}`

const toStrings = (values: Translation): LanguageValues<string> => byLanguage((language) => values?.[language] ?? '')

export const sameTranslation = (a: Translation, b: Translation) => categoryLanguages.every((language) => (a?.[language] ?? '') === (b?.[language] ?? ''))

export const hasTranslation = (values: Translation) => categoryLanguages.some((language) => !!values?.[language]?.trim())

/** Legacy's `toTranslation`: one entry per language key present in each source, keyed by the field name. */
export const toTranslation = <Field extends string>(sources: Partial<Record<Field, Translation>>) => {
  const result: Partial<Record<string, Partial<Record<Field, string | null | undefined>>>> = {}
  ;(Object.entries(sources) as [Field, Translation][]).forEach(([field, values]) => {
    if (!values) return
    Object.entries(values).forEach(([language, value]) => {
      result[language] = { ...result[language], [field]: value } as Partial<Record<Field, string | null | undefined>>
    })
  })
  return result
}

/** Sends the stored value back unless the form changed it, so an untouched `null` or missing flag stays as it was. */
const keepUnlessChanged = (stored: unknown, value: boolean) => (!!stored === value ? (stored as boolean) : value)

export const readFeatureNames = (languageMaps: LanguageMaps | undefined, legacyId: number): AssignedValues =>
  assignTranslation(languageMaps, 'features', legacyId, 'name')

export const readFeatureAlternateNames = (languageMaps: LanguageMaps | undefined, legacyId: number): AssignedValues =>
  assignTranslation(languageMaps, 'features', legacyId, 'alternateName')

export const readChoiceLabels = (languageMaps: LanguageMaps | undefined, weightId: number): AssignedValues =>
  assignTranslation(languageMaps, 'choices', weightId, 'label')

const withEnglishFallback = (values: AssignedValues, fallback: unknown) =>
  byLanguage((language) => values[language] ?? (language === 'en' && typeof fallback === 'string' ? fallback : ''))

export type FeaturePosition = { storeId: string; marketId: string }

export type FeaturePositionOption = FeaturePosition & { name: string; marketIso?: string }

export const positionKey = ({ storeId, marketId }: FeaturePosition) => `${storeId}:${marketId}`

const samePosition = (a: FeaturePosition, b: FeaturePosition) => a.storeId === b.storeId && a.marketId === b.marketId

/** Legacy's `createFromArray`: every store with every market. */
export const toFeaturePositions = (stores: string[] | undefined, markets: string[] | undefined): FeaturePosition[] =>
  (stores ?? []).flatMap((storeId) => (markets ?? []).map((marketId) => ({ storeId, marketId })))

export const toSeparateLists = (positions: FeaturePosition[]) => ({
  stores: Array.from(new Set(positions.map((position) => position.storeId))),
  markets: Array.from(new Set(positions.map((position) => position.marketId))),
})

export const describePosition = (position: FeaturePosition, stores: Named[] | undefined, markets: MarketOption[] | undefined): FeaturePositionOption => {
  const store = stores?.find((candidate) => candidate.id === position.storeId)
  const market = markets?.find((candidate) => candidate.id === position.marketId)
  return { ...position, name: `${store?.name ?? position.storeId} ${market?.name ?? position.marketId}`, marketIso: market?.iso }
}

/** The store × market positions not on the feature, by name: `GET markets` answers in a different order from one read to the next. */
export const getAvailablePositions = (stores: Named[] | undefined, markets: MarketOption[] | undefined, used: FeaturePosition[]) =>
  toFeaturePositions(
    stores?.map((store) => store.id),
    markets?.map((market) => market.id)
  )
    .filter((position) => !used.some((usedPosition) => samePosition(usedPosition, position)))
    .map((position) => describePosition(position, stores, markets))
    .sort((a, b) => a.name.localeCompare(b.name))

export type FeatureChoiceValues = {
  key: string
  source?: InternalCategoryFeatureChoice
  labels: ContentLanguageTranslation
  active: boolean
  showInLists: boolean
}

export type FeatureFormValues = {
  names: ContentLanguageTranslation
  alternateNames: ContentLanguageTranslation
  active: boolean
  hidden: boolean
  hideScreenshots: boolean
  positions: FeaturePosition[]
  choices: FeatureChoiceValues[]
}

const toChoiceLabelValues = (choice: InternalCategoryFeatureChoice, languageMaps: LanguageMaps | undefined) =>
  withEnglishFallback(readChoiceLabels(languageMaps, choice.weightId), choice.choice)

export const toChoiceValues = (choice: InternalCategoryFeatureChoice, languageMaps: LanguageMaps | undefined): FeatureChoiceValues => ({
  key: `choice-${choice.weightId}`,
  source: choice,
  labels: toChoiceLabelValues(choice, languageMaps),
  active: !!choice.active,
  showInLists: !!choice.showInLists,
})

/** Legacy's new choice: active, no label yet. */
export const newChoiceValues = (): FeatureChoiceValues => ({ key: newKey('choice'), labels: toStrings(undefined), active: true, showInLists: false })

export const toFeatureFormValues = (feature: InternalCategoryFeature | undefined, languageMaps: LanguageMaps | undefined): FeatureFormValues => {
  if (!feature) {
    return {
      names: toStrings(undefined),
      alternateNames: toStrings(undefined),
      active: false,
      hidden: false,
      hideScreenshots: false,
      positions: [],
      choices: [],
    }
  }
  return {
    names: withEnglishFallback(readFeatureNames(languageMaps, feature.legacyId), feature.name),
    alternateNames: withEnglishFallback(readFeatureAlternateNames(languageMaps, feature.legacyId), feature.alternateName),
    active: !!feature.active,
    hidden: !!feature.hidden,
    hideScreenshots: !!feature.hideScreenshots,
    positions: toFeaturePositions(feature.stores, feature.markets),
    choices: feature.choices.map((choice) => toChoiceValues(choice, languageMaps)),
  }
}

/** Legacy's choice after `prepareForSave`: labels from the language maps until edited, `ordr` renumbered from 1. */
export const toChoiceSaveBody = (choice: FeatureChoiceValues, languageMaps: LanguageMaps | undefined, index: number): FeatureSaveChoice => {
  if (!choice.source) {
    const labels = toStrings(choice.labels)
    return {
      active: choice.active,
      choice: labels.en,
      labels,
      ordr: index + 1,
      translations: toTranslation({ label: labels }),
      ...(choice.showInLists ? { showInLists: true } : {}),
    }
  }
  const source = choice.source
  const changed = !sameTranslation(choice.labels, toChoiceLabelValues(source, languageMaps))
  const labels = changed ? toStrings(choice.labels) : readChoiceLabels(languageMaps, source.weightId)
  return {
    ...source,
    choice: changed ? labels.en ?? '' : source.choice,
    labels,
    translations: toTranslation({ label: labels }),
    active: keepUnlessChanged(source.active, choice.active),
    showInLists: keepUnlessChanged(source.showInLists, choice.showInLists),
    ordr: index + 1,
  }
}

/**
 * Legacy's feature body after `prepareForSave`. An update is the feature as listed with the edits; names and alternate names come
 * from the language maps until edited, and `name`/`alternateName` only change with them. A create sends only what the form set.
 */
export const toFeatureSaveBody = (
  feature: InternalCategoryFeature | undefined,
  categoryId: string,
  languageMaps: LanguageMaps | undefined,
  values: FeatureFormValues
): FeatureSaveBody => {
  const { stores, markets } = toSeparateLists(values.positions)
  const choices = values.choices.map((choice, index) => toChoiceSaveBody(choice, languageMaps, index))
  if (!feature) {
    const names = toStrings(values.names)
    const alternateNames = hasTranslation(values.alternateNames) ? toStrings(values.alternateNames) : undefined
    return {
      categoryId,
      name: names.en,
      names,
      translations: toTranslation({ name: names, alternateName: alternateNames }),
      stores,
      markets,
      choices,
      ...(alternateNames ? { alternateName: alternateNames.en } : {}),
      ...(values.active ? { active: true } : {}),
      ...(values.hidden ? { hidden: true } : {}),
      ...(values.hideScreenshots ? { hideScreenshots: true } : {}),
    }
  }
  const initial = toFeatureFormValues(feature, languageMaps)
  const namesChanged = !sameTranslation(values.names, initial.names)
  const alternateNamesChanged = !sameTranslation(values.alternateNames, initial.alternateNames)
  const names = namesChanged ? toStrings(values.names) : readFeatureNames(languageMaps, feature.legacyId)
  const alternateNames = alternateNamesChanged ? toStrings(values.alternateNames) : readFeatureAlternateNames(languageMaps, feature.legacyId)
  const { alternateNames: _listedAlternateNames, ...listed } = feature as InternalCategoryFeature & { alternateNames?: unknown }
  return {
    ...listed,
    name: namesChanged ? names.en ?? '' : feature.name,
    alternateName: alternateNamesChanged ? alternateNames.en : (feature.alternateName as string | null | undefined),
    names,
    translations: toTranslation({ name: names, alternateName: alternateNames }),
    active: keepUnlessChanged(feature.active, values.active),
    hidden: keepUnlessChanged(feature.hidden, values.hidden),
    hideScreenshots: keepUnlessChanged(feature.hideScreenshots, values.hideScreenshots),
    stores,
    markets,
    choices,
  }
}

export const canSaveFeature = (values: Pick<FeatureFormValues, 'names' | 'positions' | 'choices'>) =>
  !!values.names.en?.trim() && values.positions.length > 0 && values.choices.length > 0

export type FeatureField =
  | 'feature'
  | 'name'
  | 'names'
  | 'alternateName'
  | 'alternateNames'
  | 'active'
  | 'hidden'
  | 'hideScreenshots'
  | 'positions'
  | 'choices'
  | 'choiceLabels'

export const featureFieldLabelKeys: Record<FeatureField, string> = {
  feature: 'internal-analysts:categories_feature_field_feature',
  name: 'internal-analysts:categories_feature_field_name',
  names: 'internal-analysts:categories_feature_field_names',
  alternateName: 'internal-analysts:categories_feature_field_alternate_name',
  alternateNames: 'internal-analysts:categories_feature_field_alternate_names',
  active: 'internal-analysts:categories_feature_field_active',
  hidden: 'internal-analysts:categories_feature_field_hidden',
  hideScreenshots: 'internal-analysts:categories_feature_field_hide_screenshots',
  positions: 'internal-analysts:categories_feature_field_positions',
  choices: 'internal-analysts:categories_feature_field_choices',
  choiceLabels: 'internal-analysts:categories_feature_field_choice_labels',
}

const sameSet = (a: string[] | undefined, b: string[] | undefined) => {
  const left = new Set(a ?? [])
  const right = new Set(b ?? [])
  return left.size === right.size && Array.from(left).every((value) => right.has(value))
}

const differsIn = (stored: Translation, sent: Partial<Record<string, Partial<Record<string, string | null | undefined>>>>, field: string) =>
  categoryLanguages.some((language) => sent[language] && field in sent[language]! && (stored?.[language] ?? '') !== (sent[language]![field] ?? ''))

/** What the feature read back after a save doesn't have as sent. */
export const getUnstoredFeatureFields = (body: FeatureSaveBody, { stored, languageMaps }: FeatureSaveResult): FeatureField[] => {
  if (!stored) return ['feature']
  const fields: FeatureField[] = []
  if (stored.name !== body.name) fields.push('name')
  if (body.alternateName !== undefined && (stored.alternateName ?? '') !== (body.alternateName ?? '')) fields.push('alternateName')
  ;(['active', 'hidden', 'hideScreenshots'] as const).forEach((flag) => {
    if (!!stored[flag] !== !!body[flag]) fields.push(flag)
  })
  if (!sameSet(stored.stores, body.stores) || !sameSet(stored.markets, body.markets)) fields.push('positions')
  const storedChoices = [...stored.choices].sort((a, b) => a.ordr - b.ordr)
  const choicesDiffer =
    storedChoices.length !== body.choices.length ||
    body.choices.some((choice, index) => {
      const storedChoice = storedChoices[index]
      return (
        storedChoice.choice !== choice.choice ||
        !!storedChoice.active !== !!choice.active ||
        !!storedChoice.showInLists !== !!choice.showInLists ||
        storedChoice.ordr !== choice.ordr
      )
    })
  if (choicesDiffer) fields.push('choices')
  if (differsIn(readFeatureNames(languageMaps, stored.legacyId), body.translations, 'name')) fields.push('names')
  if (differsIn(readFeatureAlternateNames(languageMaps, stored.legacyId), body.translations, 'alternateName')) fields.push('alternateNames')
  const labelsDiffer = body.choices.some((choice, index) => {
    const storedChoice = storedChoices[index]
    return !storedChoice || differsIn(readChoiceLabels(languageMaps, storedChoice.weightId), choice.translations, 'label')
  })
  if (labelsDiffer) fields.push('choiceLabels')
  return fields
}

const keepEdit = <Value>(loaded: Value, edited: Value, current: Value, same: (a: Value, b: Value) => boolean = Object.is) =>
  same(edited, loaded) ? current : edited

const samePositions = (a: FeaturePosition[], b: FeaturePosition[]) => sameSet(a.map(positionKey), b.map(positionKey))

const sameChoiceValues = (a: FeatureChoiceValues, b: FeatureChoiceValues | undefined) =>
  !!b && a.key === b.key && a.active === b.active && a.showInLists === b.showInLists && sameTranslation(a.labels, b.labels)

const rebaseChoice = (loaded: FeatureChoiceValues | undefined, edited: FeatureChoiceValues, current: FeatureChoiceValues | undefined) =>
  loaded && current
    ? {
        ...current,
        labels: keepEdit(loaded.labels, edited.labels, current.labels, sameTranslation),
        active: keepEdit(loaded.active, edited.active, current.active),
        showInLists: keepEdit(loaded.showInLists, edited.showInLists, current.showInLists),
      }
    : edited

/**
 * The form's edits from `loaded` applied onto `current`, the feature as read again right before saving: every field the form left
 * as loaded takes its current value, so a save doesn't put back what was stored meanwhile. An edited choice list keeps the form's
 * choices and order, each choice's untouched fields taking their current values.
 */
export const rebaseFeatureFormValues = (loaded: FeatureFormValues, edited: FeatureFormValues, current: FeatureFormValues): FeatureFormValues => {
  const choicesEdited =
    loaded.choices.length !== edited.choices.length || edited.choices.some((choice, index) => !sameChoiceValues(choice, loaded.choices[index]))
  const loadedChoices = new Map(loaded.choices.map((choice) => [choice.key, choice]))
  const currentChoices = new Map(current.choices.map((choice) => [choice.key, choice]))
  return {
    names: keepEdit(loaded.names, edited.names, current.names, sameTranslation),
    alternateNames: keepEdit(loaded.alternateNames, edited.alternateNames, current.alternateNames, sameTranslation),
    active: keepEdit(loaded.active, edited.active, current.active),
    hidden: keepEdit(loaded.hidden, edited.hidden, current.hidden),
    hideScreenshots: keepEdit(loaded.hideScreenshots, edited.hideScreenshots, current.hideScreenshots),
    positions: keepEdit(loaded.positions, edited.positions, current.positions, samePositions),
    choices: choicesEdited
      ? edited.choices.map((choice) => rebaseChoice(loadedChoices.get(choice.key), choice, currentChoices.get(choice.key)))
      : current.choices,
  }
}

export type ExampleImage = { id: string; url: string | null }

export type ExampleItemValues = {
  key: string
  source?: FeatureExampleItem
  type: FeatureExampleItemType
  titles: ContentLanguageTranslation
  descriptions: ContentLanguageTranslation
  image: ExampleImage | null
  imageCopyright: string
}

export type ExampleFormValues = {
  descriptions: ContentLanguageTranslation
  corrects: ExampleItemValues[]
  wrongs: ExampleItemValues[]
}

export type ExampleList = 'corrects' | 'wrongs'

export const exampleItemType: Record<ExampleList, FeatureExampleItemType> = { corrects: 'correct', wrongs: 'wrong' }

const translationOrEnglish = (values: Translation, english: string | undefined) =>
  values ? toStrings(values) : byLanguage((language) => (language === 'en' ? english ?? '' : ''))

/** Legacy's `createNew` for a position without an example: the market as its ISO code. */
export const newExample = (storeId: string, marketIso: string): FeatureExample => ({ corrects: [], wrongs: [], store: storeId, market: marketIso })

export const toExampleItemValues = (item: FeatureExampleItem): ExampleItemValues => ({
  key: item.itemId != null ? `item-${item.itemId}` : newKey('item'),
  source: item,
  type: item.type,
  titles: translationOrEnglish(item.titles, item.title),
  descriptions: translationOrEnglish(item.descriptions, item.desc),
  image: item.imageId ? { id: item.imageId, url: item.imageUrl } : null,
  imageCopyright: item.imageCopyright ?? '',
})

export const newExampleItemValues = (list: ExampleList): ExampleItemValues => ({
  key: newKey('item'),
  type: exampleItemType[list],
  titles: toStrings(undefined),
  descriptions: toStrings(undefined),
  image: null,
  imageCopyright: '',
})

/** Legacy's item after "Done": the image's id and url, `title`/`desc` replaced only with their translations. */
export const toExampleItemBody = (values: ExampleItemValues): FeatureExampleItem => {
  const image = { imageId: values.image?.id ?? null, imageUrl: values.image?.url ?? null }
  if (!values.source) {
    const titles = hasTranslation(values.titles) ? toStrings(values.titles) : undefined
    const descriptions = hasTranslation(values.descriptions) ? toStrings(values.descriptions) : undefined
    return {
      title: titles?.en ?? '',
      desc: descriptions?.en ?? '',
      ...image,
      imageCopyright: values.imageCopyright || null,
      type: values.type,
      ...(titles ? { titles } : {}),
      ...(descriptions ? { descriptions } : {}),
    }
  }
  const source = values.source
  const initial = toExampleItemValues(source)
  const titlesChanged = !sameTranslation(values.titles, initial.titles)
  const descriptionsChanged = !sameTranslation(values.descriptions, initial.descriptions)
  return {
    ...source,
    ...(titlesChanged ? { titles: toStrings(values.titles), title: values.titles.en ?? '' } : {}),
    ...(descriptionsChanged ? { descriptions: toStrings(values.descriptions), desc: values.descriptions.en ?? '' } : {}),
    ...image,
    imageCopyright: values.imageCopyright === initial.imageCopyright ? source.imageCopyright : values.imageCopyright,
    type: values.type,
  }
}

export const toExampleFormValues = (example: FeatureExample): ExampleFormValues => ({
  descriptions: translationOrEnglish(example.descriptions, example.desc),
  corrects: example.corrects.map(toExampleItemValues),
  wrongs: example.wrongs.map(toExampleItemValues),
})

export const toExampleSaveBody = (example: FeatureExample, values: ExampleFormValues): FeatureExample => {
  const descriptionsChanged = !sameTranslation(values.descriptions, toExampleFormValues(example).descriptions)
  return {
    ...example,
    ...(descriptionsChanged ? { descriptions: toStrings(values.descriptions), desc: values.descriptions.en ?? '' } : {}),
    corrects: values.corrects.map(toExampleItemBody),
    wrongs: values.wrongs.map(toExampleItemBody),
  }
}

export type ExampleDraft = {
  position: FeaturePosition
  marketIso: string
  positionName: string
  example: FeatureExample
}

export const findDraftForPosition = (drafts: ExampleDraft[], position: FeaturePosition) => drafts.find((draft) => samePosition(draft.position, position))

export const findDraftForExample = (drafts: ExampleDraft[], example: FeatureExample | null | undefined) =>
  example?.id ? drafts.find((draft) => draft.example.id === example.id) : undefined

/** One draft per stored example (the backend answers the same example for every market) and per position for new ones. */
export const upsertExampleDraft = (drafts: ExampleDraft[], draft: ExampleDraft) => {
  const index = drafts.findIndex((candidate) =>
    draft.example.id ? candidate.example.id === draft.example.id : !candidate.example.id && samePosition(candidate.position, draft.position)
  )
  if (index === -1) return [...drafts, draft]
  return drafts.map((candidate, candidateIndex) =>
    candidateIndex === index ? { ...draft, position: candidate.position, positionName: candidate.positionName, marketIso: candidate.marketIso } : candidate
  )
}

export type ExampleField = 'example' | 'description' | 'corrects' | 'wrongs'

export const exampleFieldLabelKeys: Record<ExampleField, string> = {
  example: 'internal-analysts:categories_example_field_example',
  description: 'internal-analysts:categories_example_field_description',
  corrects: 'internal-analysts:categories_example_corrects',
  wrongs: 'internal-analysts:categories_example_wrongs',
}

const itemDiffers = (stored: FeatureExampleItem | undefined, sent: FeatureExampleItem) =>
  !stored ||
  stored.title !== sent.title ||
  (stored.desc ?? '') !== (sent.desc ?? '') ||
  (stored.imageId ?? null) !== (sent.imageId ?? null) ||
  (stored.imageCopyright ?? '') !== (sent.imageCopyright ?? '') ||
  stored.type !== sent.type ||
  (!!sent.titles && !sameTranslation(stored.titles, sent.titles)) ||
  (!!sent.descriptions && !sameTranslation(stored.descriptions, sent.descriptions))

const listDiffers = (stored: FeatureExampleItem[] | undefined, sent: FeatureExampleItem[]) =>
  (stored ?? []).length !== sent.length || sent.some((item, index) => itemDiffers(stored?.[index], item))

/** What the example read back at its position doesn't have as sent. */
export const getUnstoredExampleFields = (body: FeatureExample, stored: FeatureExample | null | undefined): ExampleField[] => {
  if (!stored || (body.id && stored.id !== body.id)) return ['example']
  const fields: ExampleField[] = []
  if ((stored.desc ?? '') !== (body.desc ?? '') || (!!body.descriptions && !sameTranslation(stored.descriptions, body.descriptions))) fields.push('description')
  if (listDiffers(stored.corrects, body.corrects)) fields.push('corrects')
  if (listDiffers(stored.wrongs, body.wrongs)) fields.push('wrongs')
  return fields
}

export const featureEditPath = (categoryId: string, featureId?: string) => `/internal/categories/${categoryId}/features${featureId ? `/${featureId}` : ''}`
